import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, catchError, timeout } from "rxjs/operators";
import { HttpMarshClientService } from "./http-marsh-client.service";
import type {
  MarshCatalogItem,
  MarshIssuePolicyResponse,
  MarshIssueRequest,
  MarshIssueResult,
  MarshQuoteAllResponse,
  MarshQuoteRequest,
  MarshStatus,
  MarshVehicleVersion,
} from "./marsh-insurance.types";

const QUOTE_TIMEOUT_MS = 120_000;

/**
 * Service for Marsh insurance broker API.
 * Endpoints: POST brands, subbrands, versions, quote_all, issue_policy.
 */
@Injectable({
  providedIn: "root",
})
export class MarshService {
  private readonly basePath = "/api/marsh_api_service/v1/insurance";

  constructor(private http: HttpMarshClientService) {}

  getBrands(year: number | string): Observable<MarshCatalogItem[]> {
    return this.http.post(`${this.basePath}/brands`, { year }).pipe(
      map((res: { status?: string; data?: MarshCatalogItem[] }) => res?.data ?? []),
      catchError(() => of([]))
    );
  }

  getSubbrands(brand: string, year: number | string): Observable<MarshCatalogItem[]> {
    return this.http.post(`${this.basePath}/subbrands`, { brand, year }).pipe(
      map((res: { status?: string; data?: MarshCatalogItem[] }) => res?.data ?? []),
      catchError(() => of([]))
    );
  }

  getVersions(brand: string, subbrand: string, year: number | string): Observable<MarshVehicleVersion[]> {
    return this.http.post(`${this.basePath}/versions`, { brand, subbrand, year }).pipe(
      map((res: { status?: string; data?: MarshVehicleVersion[] }) => res?.data ?? []),
      catchError(() => of([]))
    );
  }

  quoteAll(payload: { quote: MarshQuoteRequest }): Observable<MarshQuoteAllResponse> {
    return this.http.post(`${this.basePath}/quote_all`, payload).pipe(
      timeout(QUOTE_TIMEOUT_MS),
      map(
        (res: MarshQuoteAllResponse) => res ?? { status: "ERROR" as MarshStatus, data: [], errors: ["Empty response"] }
      ),
      catchError(() => of({ status: "ERROR" as MarshStatus, data: [], errors: ["Request failed"] }))
    );
  }

  issuePolicy(payload: { issue: MarshIssueRequest }): Observable<MarshIssueResult> {
    return this.http.post(`${this.basePath}/issue_policy`, payload).pipe(
      map((res: MarshIssuePolicyResponse) => this.normalizeIssueResponse(res)),
      catchError(() => of({ policy_number: "", policy_url: "" }))
    );
  }

  private normalizeIssueResponse(res: MarshIssuePolicyResponse): MarshIssueResult {
    const data = res?.data ?? (res as unknown as MarshIssueResult);
    return {
      policy_number: String(data?.policy_number ?? ""),
      policy_url: String(data?.policy_url ?? ""),
      credit_number: data?.credit_number != null ? String(data.credit_number) : undefined,
    };
  }
}
